import {
  SurveyQuestion,
  SurveyQuestionType,
  DecimalProps,
} from '@fdha/graphql-api-admin';
import { SURVEY_FOOTNOTE_MARKER } from '@fdha/common-utils';
import {
  CheckboxGroupState,
  DecimalTextFieldV2,
  theme,
  TimeInputV2,
} from '@fdha/web-ui-library';
import React, { useCallback, useMemo, useState } from 'react';
import { isValid } from 'date-fns';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { grey } from '@mui/material/colors';

import { QuestionField } from '../../../../components';

interface SurveyQuestionViewProps {
  questionNumber: number;
  item: SurveyQuestion;
  error?: string;
  onAnswerChange?: (questionId: string, answer: string | string[]) => void;
}

const SurveyQuestionView: React.FC<SurveyQuestionViewProps> = ({
  questionNumber,
  item,
  error,
  onAnswerChange,
}) => {
  const [answer, setAnswer] = useState<string | string[]>(item.answer || '');

  const enabled = !!onAnswerChange;

  const possibleAnswers = useMemo(
    () =>
      item.possibleAnswers?.map((possibleAnswer, index) => ({
        label: possibleAnswer,
        value: possibleAnswer,
        description:
          item.possibleAnswersDescriptions?.[index]?.trim() || undefined,
      })) ?? [],
    [item.possibleAnswers, item.possibleAnswersDescriptions]
  );

  const hasAnswerDescriptions = possibleAnswers.some(
    (option) => !!option.description
  );

  const singleAnswer =
    typeof answer === 'string' ? answer : item.answer?.[0] || '';
  const placeholder = item.placeholderText || '';

  const commonProps = {
    number: questionNumber + 1,
    title: item.title,
    helperText: item.helpText || undefined,
    showFootnoteMarker: !!item.footnote,
    dataTestIdPrefix: 'SURVEY',
  };

  const timeValue = useMemo(() => {
    // Use the local state answer if it's a string, otherwise fall back to item.answer
    const timeAnswer =
      typeof answer === 'string' && answer ? answer : item.answer?.[0] || '';

    if (typeof timeAnswer === 'string' && timeAnswer) {
      const parts = timeAnswer.split(' ');
      if (parts.length === 2) {
        return {
          time: parts[0] || '',
          dayPeriod: (parts[1]?.toLowerCase() === 'pm' ? 'pm' : 'am') as
            | 'am'
            | 'pm',
        };
      }
    }
    return { time: '', dayPeriod: 'am' as const };
  }, [item.answer, answer]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setAnswer(newValue);
    onAnswerChange?.(item.id, newValue);
  };

  const handleMultipleChoiceChange = (newValues: CheckboxGroupState) => {
    const selectedValues = Object.keys(newValues).filter(
      (key) => newValues[key]
    );
    setAnswer(selectedValues);
    onAnswerChange?.(item.id, selectedValues);
  };

  const handleSingleChoiceChange = (newValue: string) => {
    setAnswer(newValue);
    onAnswerChange?.(item.id, newValue);
  };

  const handleDateChange = (date: Date | null) => {
    let newValue = '';
    if (date) {
      if (isValid(date)) {
        newValue = date.toISOString();
      } else {
        newValue = date.toString();
      }
    }
    setAnswer(newValue);
    onAnswerChange?.(item.id, newValue);
  };

  const handleTimeChange = (field: string, value: string) => {
    const newTimeValue =
      field === 'time'
        ? { time: value, dayPeriod: timeValue.dayPeriod }
        : { time: timeValue.time, dayPeriod: value as 'am' | 'pm' };

    const formattedTime = `${newTimeValue.time} ${newTimeValue.dayPeriod}`;
    setAnswer(formattedTime);
    onAnswerChange?.(item.id, formattedTime);
  };

  const renderQuestionHeader = useCallback(
    () => (
      <QuestionField
        type="openText"
        textFieldProps={{
          value: '',
          style: { display: 'none' }, // Hide the text field
        }}
        enabled={false}
        {...commonProps}
      />
    ),
    [commonProps]
  );

  const renderQuestion = () => {
    switch (item.type) {
      case SurveyQuestionType.OpenText:
      case SurveyQuestionType.Meal:
      case SurveyQuestionType.Number:
      case SurveyQuestionType.Snack: {
        const multiline = item.type === SurveyQuestionType.OpenText;

        return (
          <QuestionField
            type="openText"
            textFieldProps={{
              value: singleAnswer,
              type: item.type,
              multiline,
              placeholder,
              error: !!error,
              helperText: error,
              onChange: handleInputChange,
            }}
            enabled={!!onAnswerChange}
            {...commonProps}
          />
        );
      }
      case SurveyQuestionType.Time: {
        return (
          <>
            {renderQuestionHeader()}
            <>
              {onAnswerChange ? (
                <TimeInputV2
                  key={`${item.id}-time-field`}
                  title=""
                  time={timeValue.time}
                  dayPeriod={timeValue.dayPeriod}
                  error={error}
                  onChange={handleTimeChange}
                  onBlur={() => {}}
                  caption="Format: HH:MM (e.g., 09:30)"
                />
              ) : (
                <Box
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: grey[100],
                      '& fieldset': { borderColor: theme.palette.divider },
                    },
                    '& .MuiToggleButton-root': {
                      color: alpha(theme.palette.text.primary, 0.38),
                      borderColor: theme.palette.divider,
                      backgroundColor: grey[100],
                    },
                    pointerEvents: 'none',
                  }}
                >
                  <TimeInputV2
                    title=""
                    time={timeValue.time}
                    dayPeriod={timeValue.dayPeriod}
                    error={undefined}
                    onChange={() => {}}
                  />
                </Box>
              )}
            </>
          </>
        );
      }
      case SurveyQuestionType.Decimal: {
        const decimalProps = (item.customPropsOfType as DecimalProps) || {};
        const hasRangeConstraints =
          decimalProps?.min !== undefined && decimalProps?.max !== undefined;

        let helperText = error;
        if (!error && hasRangeConstraints) {
          const unitDisplay = decimalProps.unit ? ` ${decimalProps.unit}` : '';
          helperText = `Please insert a value between ${decimalProps.min} - ${decimalProps.max}${unitDisplay}`;
        }

        const handleDecimalChange = (value: string) => {
          setAnswer(value);
          onAnswerChange?.(item.id, value);
        };

        const numberOfDecimals = decimalProps.numberOfDecimals ?? 1;

        if (enabled) {
          return (
            <Box>
              {renderQuestionHeader()}
              <DecimalTextFieldV2
                numberOfDecimals={numberOfDecimals}
                maxLength={item.maxLength ?? 3}
                unit={decimalProps.unit}
                handleChange={handleDecimalChange}
                value={singleAnswer}
                placeholder={placeholder}
                error={!!error}
                helperText={error || helperText}
              />
            </Box>
          );
        } else {
          return (
            <QuestionField
              type="openText"
              textFieldProps={{
                value: singleAnswer,
                placeholder,
                error: !!error,
                helperText: error || helperText,
                endContent: decimalProps.unit && (
                  <Typography
                    variant="body2"
                    color={theme.palette.text.secondary}
                    data-testid="TEXT_FIELD_UNIT"
                  >
                    {decimalProps.unit}
                  </Typography>
                ),
              }}
              enabled={false}
              {...commonProps}
            />
          );
        }
      }
      case SurveyQuestionType.MultipleChoice: {
        const multipleChoiceAnswer =
          possibleAnswers?.reduce<CheckboxGroupState>((acc, { value }) => {
            acc[value] = Array.isArray(answer)
              ? answer.includes(value)
              : item.answer?.includes(value) ?? false;
            return acc;
          }, {}) ?? {};

        return (
          <QuestionField
            type="multipleChoice"
            checkboxGroupProps={{
              options: possibleAnswers,
              value: multipleChoiceAnswer,
              error,
              onChange: handleMultipleChoiceChange,
            }}
            enabled={enabled}
            {...commonProps}
          />
        );
      }
      case SurveyQuestionType.SingleChoice:
      case SurveyQuestionType.Binary:
        return (
          <QuestionField
            type="singleChoice"
            radioGroupProps={{
              options: possibleAnswers,
              value: singleAnswer,
              placeholder,
              error,
              onChange: handleSingleChoiceChange,
              boldLabelWithDescription: hasAnswerDescriptions,
            }}
            enabled={enabled}
            {...commonProps}
          />
        );
      case SurveyQuestionType.Scale:
        return (
          <QuestionField
            type="scale"
            scaleProps={{
              options: possibleAnswers,
              value: singleAnswer,
              error,
              onChange: handleSingleChoiceChange,
            }}
            enabled={enabled}
            {...commonProps}
          />
        );
      case SurveyQuestionType.Date:
        return (
          <QuestionField
            type="date"
            datePickerProps={{
              value: new Date(singleAnswer),
              error: !!error,
              helperText: error,
              onChange: handleDateChange,
            }}
            enabled={enabled}
            {...commonProps}
          />
        );
    }
  };

  return (
    <>
      {renderQuestion()}
      {!!item.footnote && (
        <Typography
          variant="caption"
          color={theme.palette.text.secondary}
          display="block"
          mt={1}
          data-testid="SURVEY_QUESTION_FOOTNOTE"
        >
          <sup aria-hidden="true">{SURVEY_FOOTNOTE_MARKER}</sup>
          {item.footnote}
        </Typography>
      )}
    </>
  );
};

export default SurveyQuestionView;
